import React, { useState, useRef, useEffect } from 'react';
import { Company, CompanyCreate, CompanyUpdate, CompanyStripeSettingsUpdate } from '../types';
import { apiService } from '../services/api';
import '../index.css';
import { CompanyLogoUpload } from './CompanyLogoUpload';
import { CompanyBasicInfo } from './CompanyBasicInfo';
import { CompanyBrandColors } from './CompanyBrandColors';
import { CompanyContactInfo } from './CompanyContactInfo';
import { CompanyAddress } from './CompanyAddress';
import { CompanyLegalInfo } from './CompanyLegalInfo';
import { CompanyBankingInfo } from './CompanyBankingInfo';
import { CompanyStripeSettings } from './CompanyStripeSettings';
import { CompanyFooterInfo } from './CompanyFooterInfo';
import { SecretKeyModal } from './SecretKeyModal';

interface CompanyFormProps {
  initialData?: Company | null;
  onSubmit: (data: CompanyCreate | CompanyUpdate) => void;
  onCancel: () => void;
}

const CompanyFormRefactored: React.FC<CompanyFormProps> = ({ initialData, onSubmit, onCancel }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(initialData?.logo_url || '/eclogo-check.svg');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [loadingStripeSettings, setLoadingStripeSettings] = useState(false);
  const [savingStripeSettings, setSavingStripeSettings] = useState(false);
  const [validatingStripeSettings, setValidatingStripeSettings] = useState(false);
  const [stripeFeedback, setStripeFeedback] = useState<string>('');
  const [stripeValidationFeedback, setStripeValidationFeedback] = useState<string>('');
  const [showSecretKeyModal, setShowSecretKeyModal] = useState(false);
  const [secretKeyEnv, setSecretKeyEnv] = useState<'test' | 'production'>('test');
  const [secretKey, setSecretKey] = useState<string>('');
  const [stripeSettings, setStripeSettings] = useState({
    stripe_enabled: false,
    stripe_active_environment: 'test' as 'test' | 'production',
    has_stripe_test_secret_key: false,
    has_stripe_production_secret_key: false,
    stripe_test_secret_key_display: '',
    stripe_production_secret_key_display: '',
  });
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    company_number: initialData?.company_number || '',
    contact_name: initialData?.contact_name || '',
    contact_role: initialData?.contact_role || '',
    address_street: initialData?.address_street || '',
    address_postcode: initialData?.address_postcode || '',
    address_city: initialData?.address_city || '',
    address_country: initialData?.address_country || '',
    address_state: initialData?.address_state || '',
    telephone_number: initialData?.telephone_number || '',
    email_address: initialData?.email_address || '',
    website_url: initialData?.website_url || '',
    tax_id: initialData?.tax_id || '',
    vat_number: initialData?.vat_number || '',
    registration_number: initialData?.registration_number || '',
    bank_name: initialData?.bank_name || '',
    bank_account: initialData?.bank_account || '',
    bank_iban: initialData?.bank_iban || '',
    bank_bic: initialData?.bank_bic || '',
    bank_sort_code: initialData?.bank_sort_code || '',
    legal_form: initialData?.legal_form || '',
    managing_director: initialData?.managing_director || '',
    court_registration: initialData?.court_registration || '',
    court_location: initialData?.court_location || '',
    fax_number: initialData?.fax_number || '',
    support_email: initialData?.support_email || '',
    billing_email: initialData?.billing_email || '',
    footer_legal_text: initialData?.footer_legal_text || '',
    footer_payment_terms: initialData?.footer_payment_terms || '',
    language: initialData?.language || 'en',
    is_active: initialData?.is_active !== undefined ? initialData.is_active : true,
    logo_url: initialData?.logo_url || '/eclogo-check.svg',
    brand_color_primary: initialData?.brand_color_primary || '#4176ba',
    brand_color_secondary: initialData?.brand_color_secondary || '#349b94',
  });

  useEffect(() => {
    const companyId = initialData?.id;
    if (!companyId) {
      return;
    }

    let cancelled = false;
    const loadStripeSettings = async () => {
      try {
        setLoadingStripeSettings(true);
        setStripeFeedback('');
        const settings = await apiService.companies.getStripePaymentSettings(companyId);
        if (cancelled) return;

        setStripeSettings(prev => ({
          ...prev,
          stripe_enabled: settings.stripe_enabled,
          stripe_active_environment: settings.stripe_active_environment,
          has_stripe_test_secret_key: !!settings.has_stripe_test_secret_key,
          has_stripe_production_secret_key: !!settings.has_stripe_production_secret_key,
          // Note: In production, these would come from a secure endpoint that returns censored values
          // For now, we show generic placeholders since the actual keys are never returned from backend
          stripe_test_secret_key_display: settings.has_stripe_test_secret_key ? 'rk_test_*****' : '',
          stripe_production_secret_key_display: settings.has_stripe_production_secret_key ? 'rk_live_*****' : '',
        }));
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading Stripe settings:', error);
          setStripeFeedback('Failed to load Stripe settings.');
        }
      } finally {
        if (!cancelled) {
          setLoadingStripeSettings(false);
        }
      }
    };

    loadStripeSettings();
    return () => {
      cancelled = true;
    };
  }, [initialData?.id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) {
        alert('Please upload a valid image file (JPEG, PNG, GIF, or SVG)');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }

      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile || !initialData?.id) return;

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', logoFile);
      
      const response = await apiService.uploadCompanyLogo(initialData.id, formData);
      if (response.logo_url) {
        setLogoPreview(response.logo_url);
        setFormData(prev => ({ ...prev, logo_url: response.logo_url }));
      }
      alert('Logo uploaded successfully');
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleStripeChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setStripeSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const buildStripeUpdatePayload = (): CompanyStripeSettingsUpdate => {
    const payload: CompanyStripeSettingsUpdate = {
      stripe_enabled: stripeSettings.stripe_enabled,
      stripe_active_environment: stripeSettings.stripe_active_environment,
      // Secret keys are NEVER included in frontend payloads
    };

    return payload;
  };

  const getApiErrorMessage = (error: unknown, fallbackMessage: string): string => {
    const detail = (error as any)?.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) {
      return detail.trim();
    }

    const message = (error as any)?.message;
    if (typeof message === 'string' && message.trim()) {
      return message.trim();
    }

    return fallbackMessage;
  };

  const handleSaveStripeSettings = async () => {
    if (!initialData?.id) return;
    try {
      setSavingStripeSettings(true);
      setStripeFeedback('');
      const updated = await apiService.companies.updateStripePaymentSettings(
        initialData.id,
        buildStripeUpdatePayload()
      );

      setStripeSettings(prev => ({
        ...prev,
        has_stripe_test_secret_key: !!updated.has_stripe_test_secret_key,
        has_stripe_production_secret_key: !!updated.has_stripe_production_secret_key,
      }));
      setStripeFeedback('Stripe settings saved.');
    } catch (error) {
      console.error('Error saving Stripe settings:', error);
      setStripeFeedback(getApiErrorMessage(error, 'Failed to save Stripe settings.'));
    } finally {
      setSavingStripeSettings(false);
    }
  };

  const openSecretKeyModal = (env: 'test' | 'production') => {
    setSecretKeyEnv(env);
    setSecretKey('');
    setShowSecretKeyModal(true);
  };

  const handleUpdateSecretKey = async () => {
    if (!initialData?.id) return;
    try {
      setValidatingStripeSettings(true);
      setStripeValidationFeedback('');
      
      // Build payload with secret key for the current modal environment
      // Following Stripe docs: use restricted keys (rk_test_*, rk_live_*)
      const payload: CompanyStripeSettingsUpdate = {
        stripe_active_environment: secretKeyEnv,
      };
      
      if (secretKey.trim()) {
        if (secretKeyEnv === 'test') {
          payload.stripe_test_secret_key = secretKey.trim();
        } else {
          payload.stripe_production_secret_key = secretKey.trim();
        }
      }
      
      const validation = await apiService.companies.validateStripePaymentSettings(
        initialData.id,
        payload
      );
      
      if (validation.success) {
        setStripeValidationFeedback(validation.message);
        await loadStripeSettings();
        setShowSecretKeyModal(false);
        setSecretKey('');
      } else {
        setStripeValidationFeedback(validation.message);
      }
    } catch (error) {
      console.error('Error updating secret key:', error);
      setStripeValidationFeedback(getApiErrorMessage(error, 'Failed to update key.'));
    } finally {
      setValidatingStripeSettings(false);
    }
  };

  const handleValidateStripeSettings = async () => {
    if (!initialData?.id) return;
    try {
      setValidatingStripeSettings(true);
      setStripeValidationFeedback('');
      const validation = await apiService.companies.validateStripePaymentSettings(
        initialData.id,
        buildStripeUpdatePayload()
      );
      setStripeValidationFeedback(validation.message);
    } catch (error) {
      console.error('Error validating Stripe settings:', error);
      setStripeValidationFeedback(getApiErrorMessage(error, 'Credential validation failed.'));
    } finally {
      setValidatingStripeSettings(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // If there's a new logo file and we're creating a new company,
    // we'll need to upload it after the company is created
    const submitData: CompanyCreate | CompanyUpdate = { ...formData };
    
    // Remove logo_url if it's just a preview from a file upload
    if (logoFile && !initialData && 'logo_url' in submitData) {
      // For new companies with file uploads, we don't send logo_url
      const { logo_url, ...dataWithoutLogo } = submitData;
      onSubmit(dataWithoutLogo);
    } else {
      onSubmit(submitData);
    }
  };

  const loadStripeSettings = async () => {
    if (!initialData?.id) return;
    
    try {
      setLoadingStripeSettings(true);
      setStripeFeedback('');
      const settings = await apiService.companies.getStripePaymentSettings(initialData.id);
      setStripeSettings(prev => ({
        ...prev,
        stripe_enabled: settings.stripe_enabled,
        stripe_active_environment: settings.stripe_active_environment,
        has_stripe_test_secret_key: !!settings.has_stripe_test_secret_key,
        has_stripe_production_secret_key: !!settings.has_stripe_production_secret_key,
        stripe_test_secret_key_display: settings.has_stripe_test_secret_key ? 'rk_test_*****' : '',
        stripe_production_secret_key_display: settings.has_stripe_production_secret_key ? 'rk_live_*****' : '',
      }));
    } catch (error) {
      console.error('Error loading Stripe settings:', error);
      setStripeFeedback('Failed to load Stripe settings.');
    } finally {
      setLoadingStripeSettings(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="form">
      <CompanyLogoUpload
        logoPreview={logoPreview}
        logoFile={logoFile}
        uploadingLogo={uploadingLogo}
        initialData={initialData}
        fileInputRef={fileInputRef}
        handleLogoChange={handleLogoChange}
        handleLogoUpload={handleLogoUpload}
        setLogoPreview={setLogoPreview}
        setLogoFile={setLogoFile}
        setFormData={setFormData}
      />

      <CompanyBasicInfo
        formData={formData}
        handleChange={handleChange}
      />

      <CompanyBrandColors
        formData={formData}
        handleChange={handleChange}
        setFormData={setFormData}
      />

      <CompanyContactInfo
        formData={formData}
        handleChange={handleChange}
      />

      <CompanyAddress
        formData={formData}
        handleChange={handleChange}
      />

      <CompanyLegalInfo
        formData={formData}
        handleChange={handleChange}
      />

      <CompanyBankingInfo
        formData={formData}
        handleChange={handleChange}
      />

      <CompanyStripeSettings
        initialData={initialData}
        loadingStripeSettings={loadingStripeSettings}
        stripeSettings={stripeSettings}
        stripeFeedback={stripeFeedback}
        stripeValidationFeedback={stripeValidationFeedback}
        validatingStripeSettings={validatingStripeSettings}
        savingStripeSettings={savingStripeSettings}
        handleStripeChange={handleStripeChange}
        handleValidateStripeSettings={handleValidateStripeSettings}
        handleSaveStripeSettings={handleSaveStripeSettings}
        openSecretKeyModal={openSecretKeyModal}
      />

      <CompanyFooterInfo
        formData={formData}
        handleChange={handleChange}
      />

      <div className="form-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
          />
          Active
        </label>
      </div>

      <div className="form-actions">
        <button type="submit" className="btn btn-primary">
          {initialData ? 'Update' : 'Create'} Company
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>

      <SecretKeyModal
        show={showSecretKeyModal}
        environment={secretKeyEnv}
        secretKey={secretKey}
        isValidating={validatingStripeSettings}
        onClose={() => setShowSecretKeyModal(false)}
        onEnvironmentChange={setSecretKeyEnv}
        onSecretKeyChange={setSecretKey}
        onSubmit={handleUpdateSecretKey}
      />
    </form>
  );
}

export default CompanyFormRefactored;